import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { trackSubscriptionComplete } from '../utils/analytics';
import './SubscriptionSuccess.css';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [verifiedTier, setVerifiedTier] = useState(null);
  const purchaseTrackedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // First verify the checkout session to get the intended tier
      verifyCheckoutSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const verifyCheckoutSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Verify the checkout session to get the tier they just subscribed to
      const verifyResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/subscription/verify-session?session_id=${sessionId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (verifyResponse.ok) {
        const sessionData = await verifyResponse.json();
        setVerifiedTier(sessionData);

        // Fire the GA4 purchase event + Google Ads conversion tag exactly once
        // per checkout (guarded against StrictMode double-invoke / re-renders).
        if (!purchaseTrackedRef.current && sessionData) {
          purchaseTrackedRef.current = true;
          const planLabel = `${sessionData.tierType || 'user'}_${sessionData.tier || 'unknown'}`;
          trackSubscriptionComplete(
            planLabel,
            typeof sessionData.value === 'number' ? sessionData.value : undefined,
            sessionData.currency || 'USD'
          );
        }

        // Also fetch the full subscription status (may have updated limits)
        await fetchSubscriptionStatus();
        
        toast.success('Subscription activated successfully!');
      } else {
        // Fallback to just fetching status
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Error verifying session:', error);
      // Fallback to fetching status
      await fetchSubscriptionStatus();
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const getTierDisplayName = (tierType, tier) => {
    if (tierType === 'business') {
      const names = { starter: 'Business Starter', professional: 'Business Professional', enterprise: 'Business Enterprise' };
      return names[tier] || `Business ${tier}`;
    }
    if (tierType === 'affiliate') {
      const names = { standard: 'Standard Creator', pro: 'Pro Creator' };
      return names[tier] || tier;
    }
    const names = { free: 'Free', standard: 'Standard', premium: 'Premium' };
    return names[tier] || tier;
  };

  const displayTierType = verifiedTier?.tierType || 'user';
  const displayTier = verifiedTier?.tier || subscriptionInfo?.user?.tier || 'Premium';
  const displayLimits = verifiedTier?.limits || subscriptionInfo?.user?.limits;
  const isBusiness = displayTierType === 'business';
  const isTrialing = subscriptionInfo?.business?.status === 'trialing';

  const fmt = (v) => (v >= 999 ? 'Unlimited' : v);

  if (loading) {
    return (
      <div className="subscription-success">
        <div className="success-card loading">
          <div className="loading-spinner-large"></div>
          <p>Activating your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-success">
      <div className="success-card">
        <div className="success-icon">
          <i className={`fas ${isBusiness ? 'fa-building' : 'fa-check-circle'}`}></i>
        </div>
        
        <h1>Welcome to {getTierDisplayName(displayTierType, displayTier)}!</h1>
        
        <p className="success-message">
          {isBusiness
            ? (isTrialing
                ? 'Your 14-day free trial has started. Your business is ready to go — add employees, invite clients, and start managing your practice.'
                : 'Your business subscription is active. You can now manage employees, onboard clients, and run your practice from a single dashboard.')
            : 'Your subscription has been activated successfully. You now have access to all the features of your new plan.'}
        </p>

        {isBusiness && displayLimits ? (
          <div className="subscription-details">
            <h3>Your Business Plan</h3>
            <ul>
              <li>
                <i className="fas fa-users"></i>
                <span>Employees: <strong>up to {fmt(displayLimits.maxEmployees)}</strong></span>
              </li>
              <li>
                <i className="fas fa-user-friends"></i>
                <span>Total Clients: <strong>up to {fmt(displayLimits.maxTotalClients)}</strong></span>
              </li>
              <li>
                <i className="fas fa-user-tag"></i>
                <span>Clients per Employee: <strong>{fmt(displayLimits.maxClientsPerEmployee)}</strong></span>
              </li>
              <li>
                <i className="fas fa-qrcode"></i>
                <span>QR Code & Invite Link Onboarding</span>
              </li>
              <li>
                <i className="fas fa-clipboard-list"></i>
                <span>Client Exercise & Nutrition Assignment</span>
              </li>
              {displayLimits.ssoEnabled && (
                <li>
                  <i className="fas fa-key"></i>
                  <span>SSO / SAML Integration</span>
                </li>
              )}
            </ul>
          </div>
        ) : displayLimits ? (
          <div className="subscription-details">
            <h3>Your New Limits</h3>
            <ul>
              <li>
                <i className="fas fa-utensils"></i>
                <span>Personal Menus: <strong>{fmt(displayLimits.maxMenus)}</strong></span>
              </li>
              <li>
                <i className="fas fa-magic"></i>
                <span>MealPlanify/month: <strong>{fmt(displayLimits.maxMealPlanifyPerMonth)}</strong></span>
              </li>
              <li>
                <i className="fas fa-image"></i>
                <span>AI Generations/day: <strong>{displayLimits.maxAIGenerationsPerDay}</strong></span>
              </li>
              <li>
                <i className="fas fa-camera"></i>
                <span>Image Extractions/day: <strong>{displayLimits.maxImageExtractionsPerDay}</strong></span>
              </li>
              <li>
                <i className="fas fa-link"></i>
                <span>URL Extractions/day: <strong>{fmt(displayLimits.maxUrlExtractionsPerDay)}</strong></span>
              </li>
            </ul>
          </div>
        ) : null}

        <div className="success-actions">
          {isBusiness ? (
            <>
              <button 
                className="primary-btn"
                onClick={() => navigate('/business/dashboard')}
              >
                <i className="fas fa-tachometer-alt"></i>
                Go to Business Dashboard
              </button>
              <button 
                className="secondary-btn"
                onClick={() => navigate('/pricing')}
              >
                <i className="fas fa-cog"></i>
                Manage Subscription
              </button>
            </>
          ) : (
            <>
              <button 
                className="primary-btn"
                onClick={() => navigate('/my-menus')}
              >
                <i className="fas fa-plus"></i>
                Create a Menu
              </button>
              <button 
                className="secondary-btn"
                onClick={() => navigate('/meal-plan')}
              >
                <i className="fas fa-calendar"></i>
                Plan Your Meals
              </button>
            </>
          )}
        </div>

        <p className="manage-subscription">
          You can manage your subscription anytime from the{' '}
          <button onClick={() => navigate('/pricing')} className="link-btn">
            Pricing page
          </button>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
